/**
 * รายงานการเงิน (จอ ລາຍງານການເງິນ + ລາຍຈ່າຍ) — ตัวรวมยอดแบบ pure ทั้งหมด
 *
 * ทุกรายงาน 8 แบบคำนวณจาก `FinanceReport` ชุดเดียวที่โหลดครั้งเดียวต่อตัวกรอง (ADR-0034) —
 * ยอดรวมของทุกตารางในช่วงเดียวกันจึงตรงกันเสมอ (Σ แถว = ยอดในหน้าสรุป)
 *
 * กฎที่ห้ามพลาด:
 * - แถวรายรับที่ `tasaeng_id`/`district_id`/`province_id` เป็น null ต้องอยู่ในตาราง (กลุ่ม ບໍ່ລະບຸ…)
 *   ห้ามกรองทิ้ง ไม่งั้นยอดรวมไม่ตรงกับหน้าสรุป
 * - รายรับ = ค่าธรรมเนียม (`fee_lak`) + ອາກອນ (`stamp_lak`) นับตามวันแจ้งชำระ · รายจ่ายนับเฉพาะที่ไม่ถูกยกเลิก
 * - ตารางแต่ละแบบถูกอธิบายเป็น `ReportTable` ตัวเดียว ใช้ทั้งวาดบนจอและส่งออก Excel — ไฟล์กับจอจึงตรงกัน
 */
import type { components } from "@sdds/api-types";
import type { XlsxCell, XlsxSheet } from "./xlsx";
import { shiftDay } from "./stats-range";

export type FinanceReport = components["schemas"]["FinanceReport"];
export type RevenueRow = FinanceReport["revenue"][number];
export type ExpenseSumRow = FinanceReport["expenses"][number];
export type Expense = components["schemas"]["Expense"];
export type ExpenseWrite = components["schemas"]["ExpenseWrite"];
export type ExpenseCategory = components["schemas"]["ExpenseCategory"];

// ---------------------------------------------------------------- ป้าย

/** หมวดรายจ่ายตามลำดับที่แสดง — ต้องตรงกับ enum `ExpenseCategory` ใน openapi */
export const EXPENSE_CATEGORIES: readonly ExpenseCategory[] = [
  "utilities",
  "communication",
  "supplies",
  "fuel",
  "repair",
  "allowance",
  "meeting",
  "other",
];

export const EXPENSE_CATEGORY_LO: Record<ExpenseCategory, string> = {
  utilities: "ໄຟຟ້າ-ນ້ຳປະປາ",
  communication: "ໂທລະສັບ-ອິນເຕີເນັດ",
  supplies: "ເຄື່ອງໃຊ້ຫ້ອງການ",
  fuel: "ນ້ຳມັນ-ພາຫະນະ",
  repair: "ສ້ອມແປງ-ບຳລຸງ",
  allowance: "ເບ້ຍລ້ຽງ-ອຸດໜູນ",
  meeting: "ກອງປະຊຸມ-ຮັບແຂກ",
  other: "ອື່ນໆ",
};

/** หมวดเอกสาร A–D ตามระเบียบ · "" = ชนิดนอกระเบียบ (ไม่ระบุหมวด) อยู่ท้ายสุด */
export const DOC_CATEGORIES = ["A", "B", "C", "D", ""] as const;
const DOC_CATEGORY_LO: Record<string, string> = {
  A: "ໝວດ ກ",
  B: "ໝວດ ຂ",
  C: "ໝວດ ຄ",
  D: "ໝວດ ງ",
  "": "ບໍ່ລະບຸໝວດ",
};
export const docCategoryLabel = (code: string) => DOC_CATEGORY_LO[code] ?? `ໝວດ ${code}`;
const docCategoryOrder = (code: string) => {
  const i = (DOC_CATEGORIES as readonly string[]).indexOf(code);
  return i < 0 ? DOC_CATEGORIES.length : i;
};

export const NO_TASAENG = "ບໍ່ລະບຸຕາແສງ";
export const NO_DISTRICT = "ບໍ່ລະບຸເມືອງ";
export const NO_PROVINCE = "ບໍ່ລະບຸແຂວງ";

export const REVENUE_CAPTION = "ລາຍຮັບ ນັບຕາມວັນທີແຈ້ງຊຳລະ ສະເພາະທີ່ຊຳລະຜ່ານລະບົບ";
export const EXPENSE_CAPTION = "ລາຍຈ່າຍ ນັບສະເພາະທີ່ບັນທຶກໃນລະບົບ (ໜ້າ ລາຍຈ່າຍ)";

// ---------------------------------------------------------------- ช่วงเวลา (ตามเวลาลาว)

/** ช่วงของจอการเงิน — รายงานบัญชีคิดเป็นเดือน/ปี ไม่ใช่ "30 วันล่าสุด" เหมือน Dashboard */
export type FinRange =
  | { kind: "month" }
  | { kind: "prev" }
  | { kind: "year" }
  | { kind: "custom"; from: string; to: string };

export const FIN_PRESETS = [
  { kind: "month", label: "ເດືອນນີ້" },
  { kind: "prev", label: "ເດືອນກ່ອນ" },
  { kind: "year", label: "ປີນີ້" },
] as const;

const LAO_OFFSET_MS = 7 * 3_600_000;
const laoToday = (nowMs: number) => new Date(nowMs + LAO_OFFSET_MS).toISOString().slice(0, 10);
const laoMidnightISO = (day: string) => new Date(Date.parse(`${day}T00:00:00+07:00`)).toISOString();

/** วันแรก/วันสุดท้าย (รวมทั้งวัน) ของช่วง เป็น YYYY-MM-DD ตามเวลาลาว */
export function finRangeDays(r: FinRange, nowMs: number): { from: string; to: string } {
  const today = laoToday(nowMs);
  switch (r.kind) {
    case "month":
      return { from: `${today.slice(0, 7)}-01`, to: today };
    case "prev": {
      const firstThis = `${today.slice(0, 7)}-01`;
      const lastPrev = shiftDay(firstThis, -1);
      return { from: `${lastPrev.slice(0, 7)}-01`, to: lastPrev };
    }
    case "year":
      return { from: `${today.slice(0, 4)}-01-01`, to: today };
    case "custom":
      return { from: r.from, to: r.to };
  }
}

/** query ของ API: [00:00 วันแรก, 00:00 วันถัดจากวันสุดท้าย) — วันสุดท้ายจึงนับรวมทั้งวัน */
export function finRangeQuery(r: FinRange, nowMs: number): { from: string; to: string } {
  const d = finRangeDays(r, nowMs);
  return { from: laoMidnightISO(d.from), to: laoMidnightISO(shiftDay(d.to, 1)) };
}

export const finRangeKey = (r: FinRange) => (r.kind === "custom" ? `${r.from}_${r.to}` : r.kind);

/** อ่าน/เขียนช่วงลง query string — ไม่มี/ผิดรูป = ເດືອນນີ້ */
export function finRangeFromParams(p: URLSearchParams): FinRange {
  const from = p.get("from");
  const to = p.get("to");
  const iso = /^\d{4}-\d{2}-\d{2}$/;
  if (from && to && iso.test(from) && iso.test(to) && from <= to) return { kind: "custom", from, to };
  const period = p.get("period");
  if (period === "prev" || period === "year") return { kind: period };
  return { kind: "month" };
}

export function finRangeToParams(r: FinRange): Record<string, string> {
  if (r.kind === "custom") return { from: r.from, to: r.to };
  return r.kind === "month" ? {} : { period: r.kind };
}

// ---------------------------------------------------------------- ตารางกลาง (จอ + Excel)

export type ColKind = "text" | "code" | "int" | "money" | "pct";
export interface ReportCol {
  label: string;
  kind: ColKind;
}
/** ค่าในเซลล์ — `null` = ไม่มีค่า (เช่น % เมื่อยอดรวมเป็นศูนย์) แสดงเป็นเส้นประ ไม่ใช่ 0 */
export type ReportCell = string | number | null;
export interface ReportTable {
  cols: ReportCol[];
  rows: ReportCell[][];
  /** แถวรวม (ตัวหนา) — Σ ของแต่ละคอลัมน์ตัวเลข */
  total: ReportCell[];
}

const revenueOf = (r: RevenueRow) => r.fee_lak + r.stamp_lak;
const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 1000) / 10 : null);
const byLao = (a: string, b: string) => a.localeCompare(b, "lo");

// ---------------------------------------------------------------- 1. สรุป

export interface FinanceSummary {
  payments: number;
  fee: number;
  stamp: number;
  revenue: number;
  /** รายจ่ายต่อหมวด เฉพาะหมวดที่มีรายการ ตามลำดับ EXPENSE_CATEGORIES */
  expenseByCategory: { category: ExpenseCategory; entries: number; amount: number }[];
  expenseEntries: number;
  expense: number;
  balance: number;
}

export function summarize(rep: FinanceReport): FinanceSummary {
  let payments = 0;
  let fee = 0;
  let stamp = 0;
  for (const r of rep.revenue) {
    payments += r.payments;
    fee += r.fee_lak;
    stamp += r.stamp_lak;
  }
  const cat = new Map<ExpenseCategory, { entries: number; amount: number }>();
  for (const e of rep.expenses) {
    const c = cat.get(e.category) ?? { entries: 0, amount: 0 };
    c.entries += e.entries;
    c.amount += e.amount_lak;
    cat.set(e.category, c);
  }
  const expenseByCategory = EXPENSE_CATEGORIES.filter((c) => cat.has(c)).map((c) => ({
    category: c,
    entries: cat.get(c)?.entries ?? 0,
    amount: cat.get(c)?.amount ?? 0,
  }));
  const expense = expenseByCategory.reduce((s, c) => s + c.amount, 0);
  const expenseEntries = expenseByCategory.reduce((s, c) => s + c.entries, 0);
  const revenue = fee + stamp;
  return { payments, fee, stamp, revenue, expenseByCategory, expenseEntries, expense, balance: revenue - expense };
}

export const isEmptyReport = (rep: FinanceReport) => rep.revenue.length === 0 && rep.expenses.length === 0;

/** ตารางของแท็บสรุป (ใช้ส่งออก — บนจอแท็บนี้วาดเป็น KPI + ตารางย่อยเอง) */
export function summaryTable(s: FinanceSummary): ReportTable {
  return {
    cols: [
      { label: "ປະເພດ", kind: "text" },
      { label: "ລາຍການ", kind: "text" },
      { label: "ຈຳນວນເງິນ (ກີບ)", kind: "money" },
    ],
    rows: [
      ["ລາຍຮັບ", "ຄ່າທຳນຽມ", s.fee],
      ["ລາຍຮັບ", "ອາກອນ", s.stamp],
      ["ລາຍຮັບ", "ລາຍຮັບລວມ", s.revenue],
      ...s.expenseByCategory.map((c): ReportCell[] => ["ລາຍຈ່າຍ", EXPENSE_CATEGORY_LO[c.category], c.amount]),
      ["ລາຍຈ່າຍ", "ລາຍຈ່າຍລວມ", s.expense],
    ],
    total: ["ຍອດເຫຼືອ", "ລາຍຮັບລວມ − ລາຍຈ່າຍລວມ", s.balance],
  };
}

// ---------------------------------------------------------------- 2–4. รายรับรายจ่ายตามพื้นที่

export type PlaceLevel = "tasaeng" | "district" | "province";

interface BalanceRow {
  province: string;
  district: string;
  tasaeng: string;
  /** แถว "ບໍ່ລະບຸ…" อยู่ท้ายกลุ่ม */
  unknown: boolean;
  revenue: number;
  expense: number;
}

function placeKeyOf(
  level: PlaceLevel,
  r: { province_id: string | null; district_id: string | null; tasaeng_id: string | null },
): string {
  switch (level) {
    case "province":
      return r.province_id ?? "-";
    case "district":
      return `${r.province_id ?? "-"}|${r.district_id ?? "-"}`;
    case "tasaeng":
      // ตาแสงไม่ระบุแยกตามเมือง — ยังบอกได้ว่าเงินก้อนนั้นอยู่เมืองไหน
      return `${r.district_id ?? "-"}|${r.tasaeng_id ?? "-"}`;
  }
}

function placeNames(
  level: PlaceLevel,
  r: {
    province_id: string | null;
    province_name_lo: string;
    district_id: string | null;
    district_name_lo: string;
    tasaeng_id: string | null;
    tasaeng_name_lo: string;
  },
): Omit<BalanceRow, "revenue" | "expense"> {
  const province = r.province_id ? r.province_name_lo : NO_PROVINCE;
  const district = r.district_id ? r.district_name_lo : NO_DISTRICT;
  const tasaeng = r.tasaeng_id ? r.tasaeng_name_lo : NO_TASAENG;
  const unknown =
    level === "province" ? !r.province_id : level === "district" ? !r.district_id : !r.tasaeng_id;
  return { province, district, tasaeng, unknown };
}

function balanceRows(rep: FinanceReport, level: PlaceLevel): BalanceRow[] {
  const map = new Map<string, BalanceRow>();
  const get = (r: Parameters<typeof placeNames>[1]) => {
    const k = placeKeyOf(level, r);
    let row = map.get(k);
    if (!row) {
      row = { ...placeNames(level, r), revenue: 0, expense: 0 };
      map.set(k, row);
    }
    return row;
  };
  for (const r of rep.revenue) get(r).revenue += revenueOf(r);
  for (const e of rep.expenses) get(e).expense += e.amount_lak;
  return [...map.values()].sort(byPlace(level));
}

/** เรียงตามแม่ก่อน แล้วแถว ບໍ່ລະບຸ… ท้ายกลุ่ม แล้วตามชื่อ */
function byPlace(level: PlaceLevel) {
  type P = Omit<BalanceRow, "revenue" | "expense">;
  const parent = (r: P) => (level === "tasaeng" ? r.district : level === "district" ? r.province : "");
  const own = (r: P) => (level === "tasaeng" ? r.tasaeng : level === "district" ? r.district : r.province);
  return (a: P, b: P) =>
    byLao(parent(a), parent(b)) || Number(a.unknown) - Number(b.unknown) || byLao(own(a), own(b));
}

export function balanceTable(rep: FinanceReport, level: PlaceLevel): ReportTable {
  const rows = balanceRows(rep, level);
  const rev = rows.reduce((s, r) => s + r.revenue, 0);
  const exp = rows.reduce((s, r) => s + r.expense, 0);
  const money: ReportCol[] = [
    { label: "ລາຍຮັບ (ກີບ)", kind: "money" },
    { label: "ລາຍຈ່າຍ (ກີບ)", kind: "money" },
    { label: "ຍອດເຫຼືອ (ກີບ)", kind: "money" },
  ];
  const nums = (r: { revenue: number; expense: number }): ReportCell[] => [r.revenue, r.expense, r.revenue - r.expense];
  switch (level) {
    case "tasaeng":
      return {
        cols: [{ label: "ເມືອງ", kind: "text" }, { label: "ຕາແສງ", kind: "text" }, ...money],
        rows: rows.map((r) => [r.district, r.tasaeng, ...nums(r)]),
        total: ["ລວມ", "", ...nums({ revenue: rev, expense: exp })],
      };
    case "district":
      return {
        cols: [{ label: "ແຂວງ/ນະຄອນຫຼວງ", kind: "text" }, { label: "ເມືອງ", kind: "text" }, ...money],
        rows: rows.map((r) => [r.province, r.district, ...nums(r)]),
        total: ["ລວມ", "", ...nums({ revenue: rev, expense: exp })],
      };
    case "province":
      return {
        cols: [{ label: "ແຂວງ/ນະຄອນຫຼວງ", kind: "text" }, ...money],
        rows: rows.map((r) => [r.province, ...nums(r)]),
        total: ["ລວມ", ...nums({ revenue: rev, expense: exp })],
      };
  }
}

// ---------------------------------------------------------------- 5. รายรับตามหมวด

export function categoryTable(rep: FinanceReport): ReportTable {
  const map = new Map<string, { payments: number; fee: number; stamp: number }>();
  for (const r of rep.revenue) {
    const c = map.get(r.category_code) ?? { payments: 0, fee: 0, stamp: 0 };
    c.payments += r.payments;
    c.fee += r.fee_lak;
    c.stamp += r.stamp_lak;
    map.set(r.category_code, c);
  }
  const codes = [...map.keys()].sort((a, b) => docCategoryOrder(a) - docCategoryOrder(b) || a.localeCompare(b));
  const all = { payments: 0, fee: 0, stamp: 0 };
  for (const c of map.values()) {
    all.payments += c.payments;
    all.fee += c.fee;
    all.stamp += c.stamp;
  }
  const grand = all.fee + all.stamp;
  return {
    cols: [
      { label: "ໝວດ", kind: "text" },
      { label: "ຈຳນວນຊຳລະ", kind: "int" },
      { label: "ຄ່າທຳນຽມ (ກີບ)", kind: "money" },
      { label: "ອາກອນ (ກີບ)", kind: "money" },
      { label: "ລວມ (ກີບ)", kind: "money" },
      { label: "% ຂອງລວມ", kind: "pct" },
    ],
    rows: codes.map((code) => {
      const c = map.get(code) ?? { payments: 0, fee: 0, stamp: 0 };
      return [docCategoryLabel(code), c.payments, c.fee, c.stamp, c.fee + c.stamp, pct(c.fee + c.stamp, grand)];
    }),
    total: ["ລວມ", all.payments, all.fee, all.stamp, grand, grand > 0 ? 100 : null],
  };
}

// ---------------------------------------------------------------- 6. รายรับตามชนิดเอกสาร

export function docTypeTable(rep: FinanceReport): ReportTable {
  const map = new Map<
    string,
    { code: string; name: string; cat: string; payments: number; copies: number; fee: number; stamp: number }
  >();
  for (const r of rep.revenue) {
    const d = map.get(r.document_type_id) ?? {
      code: r.code,
      name: r.name_lo,
      cat: r.category_code,
      payments: 0,
      copies: 0,
      fee: 0,
      stamp: 0,
    };
    d.payments += r.payments;
    d.copies += r.copies;
    d.fee += r.fee_lak;
    d.stamp += r.stamp_lak;
    map.set(r.document_type_id, d);
  }
  const list = [...map.values()].sort((a, b) => b.fee + b.stamp - (a.fee + a.stamp) || a.code.localeCompare(b.code));
  const sum = (f: (d: (typeof list)[number]) => number) => list.reduce((s, d) => s + f(d), 0);
  return {
    cols: [
      { label: "ລະຫັດ", kind: "code" },
      { label: "ຊື່ເອກະສານ", kind: "text" },
      { label: "ໝວດ", kind: "text" },
      { label: "ຈຳນວນຊຳລະ", kind: "int" },
      { label: "ຈຳນວນສະບັບ", kind: "int" },
      { label: "ຄ່າທຳນຽມ (ກີບ)", kind: "money" },
      { label: "ອາກອນ (ກີບ)", kind: "money" },
      { label: "ລວມ (ກີບ)", kind: "money" },
    ],
    rows: list.map((d) => [
      d.code,
      d.name,
      docCategoryLabel(d.cat),
      d.payments,
      d.copies,
      d.fee,
      d.stamp,
      d.fee + d.stamp,
    ]),
    total: [
      "ລວມ",
      "",
      "",
      sum((d) => d.payments),
      sum((d) => d.copies),
      sum((d) => d.fee),
      sum((d) => d.stamp),
      sum((d) => d.fee + d.stamp),
    ],
  };
}

// ---------------------------------------------------------------- 7–8. รายรับแยกหมวด × พื้นที่

export function categoryMatrix(rep: FinanceReport, level: "tasaeng" | "district"): ReportTable {
  const cats = [...new Set(rep.revenue.map((r) => r.category_code))].sort(
    (a, b) => docCategoryOrder(a) - docCategoryOrder(b) || a.localeCompare(b),
  );
  const map = new Map<string, Omit<BalanceRow, "revenue" | "expense"> & { byCat: Map<string, number> }>();
  for (const r of rep.revenue) {
    const k = placeKeyOf(level, r);
    let row = map.get(k);
    if (!row) {
      row = { ...placeNames(level, r), byCat: new Map() };
      map.set(k, row);
    }
    row.byCat.set(r.category_code, (row.byCat.get(r.category_code) ?? 0) + revenueOf(r));
  }
  const rows = [...map.values()].sort(byPlace(level));
  const rowTotal = (m: Map<string, number>) => cats.reduce((s, c) => s + (m.get(c) ?? 0), 0);
  const colTotals = cats.map((c) => rows.reduce((s, r) => s + (r.byCat.get(c) ?? 0), 0));
  const head: ReportCol[] =
    level === "tasaeng"
      ? [
          { label: "ເມືອງ", kind: "text" },
          { label: "ຕາແສງ", kind: "text" },
        ]
      : [
          { label: "ແຂວງ/ນະຄອນຫຼວງ", kind: "text" },
          { label: "ເມືອງ", kind: "text" },
        ];
  return {
    cols: [
      ...head,
      ...cats.map((c): ReportCol => ({ label: `${docCategoryLabel(c)} (ກີບ)`, kind: "money" })),
      { label: "ລວມ (ກີບ)", kind: "money" },
    ],
    rows: rows.map((r) => [
      level === "tasaeng" ? r.district : r.province,
      level === "tasaeng" ? r.tasaeng : r.district,
      ...cats.map((c) => r.byCat.get(c) ?? 0),
      rowTotal(r.byCat),
    ]),
    total: ["ລວມ", "", ...colTotals, colTotals.reduce((s, v) => s + v, 0)],
  };
}

// ---------------------------------------------------------------- รายงานทั้ง 8 แบบ

export const REPORT_TABS = [
  { id: "summary", label: "ລາຍຮັບລາຍຈ່າຍ", sheet: "ລາຍຮັບລາຍຈ່າຍ" },
  { id: "tasaeng", label: "ລາຍຮັບລາຍຈ່າຍ ຕາມຕາແສງ", sheet: "ຕາມຕາແສງ" },
  { id: "district", label: "ລາຍຮັບລາຍຈ່າຍ ຕາມເມືອງ", sheet: "ຕາມເມືອງ" },
  { id: "province", label: "ລາຍຮັບລາຍຈ່າຍ ລວມ ນະຄອນຫຼວງ", sheet: "ລວມ ນະຄອນຫຼວງ" },
  { id: "category", label: "ລາຍຮັບຕາມປະເພດ", sheet: "ລາຍຮັບຕາມປະເພດ" },
  { id: "doctype", label: "ລາຍຮັບຕາມລາຍການ", sheet: "ລາຍຮັບຕາມລາຍການ" },
  { id: "cat-tasaeng", label: "ລາຍຮັບແຍກປະເພດ ຕາມ ຕາແສງ", sheet: "ແຍກປະເພດ ຕາມຕາແສງ" },
  { id: "cat-district", label: "ລາຍຮັບແຍກປະເພດ ຕາມ ເມືອງ", sheet: "ແຍກປະເພດ ຕາມເມືອງ" },
] as const;
export type ReportTabId = (typeof REPORT_TABS)[number]["id"];

export function reportTable(rep: FinanceReport, id: ReportTabId): ReportTable {
  switch (id) {
    case "summary":
      return summaryTable(summarize(rep));
    case "tasaeng":
    case "district":
    case "province":
      return balanceTable(rep, id);
    case "category":
      return categoryTable(rep);
    case "doctype":
      return docTypeTable(rep);
    case "cat-tasaeng":
      return categoryMatrix(rep, "tasaeng");
    case "cat-district":
      return categoryMatrix(rep, "district");
  }
}

// ---------------------------------------------------------------- Excel

const widthOf = (c: ReportCol) => (c.kind === "text" ? 28 : c.kind === "code" ? 12 : c.kind === "pct" ? 10 : 16);

/** ตาราง → sheet: หัว + ข้อมูล + แถวรวม (writer ไม่รองรับบรรทัดหัวเรื่อง) — รหัสส่งเป็น string เสมอ */
export function tableToSheet(name: string, t: ReportTable): XlsxSheet {
  const cell = (v: ReportCell, c: ReportCol | undefined): XlsxCell =>
    v === null ? "" : c?.kind === "code" ? String(v) : v;
  const row = (r: ReportCell[]) => r.map((v, i) => cell(v, t.cols[i]));
  return {
    name,
    header: t.cols.map((c) => c.label),
    rows: [...t.rows.map(row), row(t.total)],
    widths: t.cols.map(widthOf),
  };
}

export function reportSheets(rep: FinanceReport, ids: readonly ReportTabId[]): XlsxSheet[] {
  return ids.map((id) => {
    const tab = REPORT_TABS.find((t) => t.id === id);
    return tableToSheet(tab?.sheet ?? id, reportTable(rep, id));
  });
}

/** ชื่อไฟล์ที่ปลอดภัยกับทุกระบบไฟล์ (ตัดช่องว่าง/ทับ) */
export const safeFileName = (s: string) => s.replace(/[\s/\\:*?"<>|()]+/g, "_").replace(/_+/g, "_");
